import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import uniqueId from "lodash/uniqueId";

/** Material-UI */
import { Button, IconButton, InputAdornment, TextField, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

import ClearIcon from "@material-ui/icons/Clear";
import SearchIcon from "@material-ui/icons/Search";

const AccessibilityTooltip = React.lazy(() => import("../_common/AccessibilityTooltip"));

const useStyles = makeStyles((theme) => ({
    searchForm: {
        width: "100%",
        minHeight: 80,
        display: "flex",
        gap: 16,
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            gap: 12,
            "& > span": {
                width: "100%",
            },
        },
    },
    root: {
        maxHeight: 48,
        "& input": {
            padding: "14px 14px",
            fontSize:'clamp(14px, 1vw, 18px)',
            [theme.breakpoints.down('sm')]:{
                padding:"9px 12px 11px 12px"
            }
        },
        "& > label": {
            transform: "translate(14px, 16px) scale(1)",
            fontSize:'clamp(14px, 1vw, 16px)',
            [theme.breakpoints.down("sm")]: {
                // transform: "translate(14px, 11px) scale(1)!important",
            },
        },
    },
    clear: {
        "&:focus-visible ": {
            outline: "#000 auto 2px",
            outlineOffset: 2,
        },
    },
    btnSearch: {
        margin: 0,
        height: 48,
        minWidth: 153,
        fontSize: "clamp(12px, 1vw, 14px)",
        "& > span": {
            marginTop: 2,
        },
        "&:focus-visible": {
            outline: "#000 auto 2px",
            outlineOffset: 3,
        },
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(0, 0, 0, 0),
            width: "100%",
            height: 36,
            paddingTop: 4,
        },
    },
    visuallyhidden: {
        border: 0,
        clip: "rect(0 0 0 0)",
        height: 1,
        margin: -1,
        overflow: "hidden",
        padding: 0,
        position: "absolute",
        width: 1,
    },
    helperText:{
        [theme.breakpoints.down("sm")]:{
            margin:'-2px 0px 0px 12px',
            textTransform:'lowercase',
            opacity:0.6
        }
    }
}));

const Search = ({ onSubmit, error, search = "", label, minSearchLength }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const ID = uniqueId(`search-`);
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

    minSearchLength = isNaN(Number(minSearchLength))
        ? isNaN(Number(process.env.REACT_APP_MIN_SEARCH_LENGTH))
            ? 3
            : Number(process.env.REACT_APP_MIN_SEARCH_LENGTH)
        : Number(minSearchLength);

    const [searchText, setSearchText] = useState(search || "");

    useEffect(() => {
        setSearchText(search);
    }, [search]);

    const handleChange = (event) => {
        const value = event.target.value;
        setSearchText(value);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        onSubmit && onSubmit(searchText);
    };

    const ClearSearch = () => {
        const clear = () => {
            onSubmit && onSubmit("");
            setSearchText("");
        };
        return (
            <AccessibilityTooltip arrow={false} placement="top" title={t("Wyczyść wyszukiwarkę")}>
                <IconButton
                    size={"small"}
                    onClick={clear}
                    disableRipple
                    className={classes.clear}
                    aria-label={t("Wyczyść wyszukiwarkę")}
                >
                    <ClearIcon fontSize={"small"} color={"primary"} />
                </IconButton>
            </AccessibilityTooltip>
        );
    };

    return (
        <>
            <form className={classes.searchForm} noValidate autoComplete="off" onSubmit={handleSubmit}>
                <TextField
                    id={ID}
                    size={isMobile ? "small" : "medium"}
                    color="primary"
                    value={searchText}
                    onChange={handleChange}
                    label={label}
                    variant="outlined"
                    fullWidth
                    classes={{ root: classes.root }}
                    error={!!error}
                    helperText={
                        error
                            ? ""
                            : searchText?.length < 3
                            ? t("Wpisz minimum {{minSearchLength}} znaki", { minSearchLength })
                            : ""
                    }
                    FormHelperTextProps={{
                        className:classes.helperText
                    }}
                    InputProps={{
                        endAdornment: searchText ? (
                            <InputAdornment position="end">
                                <ClearSearch />
                            </InputAdornment>
                        ) : null,
                    }}
                />
                {/* <AccessibilityTooltip arrow={false} placement="top" title={t("Wyszukaj")}> */}
                    <Button
                        variant={"contained"}
                        color={"primary"}
                        tabIndex={0}
                        type={"submit"}
                        className={classes.btnSearch}
                        disabled={searchText?.length < minSearchLength}
                        // aria-disabled={true}
                        startIcon={<SearchIcon />}
                    >
                        {t("Wyszukaj")}
                    </Button>
                {/* </AccessibilityTooltip> */}
            </form>
        </>
    );
};

export default Search;
